import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Clock,
  Mail,
  MapPin,
  Phone,
  Ruler,
  X,
} from 'lucide-react';
import { formatLocation, formatMoney, getCemetery } from '../data/cemeteries';
import CheckoutModal from '../components/cemetery/CheckoutModal';
import PlotMap from '../components/cemetery/PlotMap';
import SelectionPanel from '../components/cemetery/SelectionPanel';
import {
  extendHolds,
  getAvailability,
  getSelection,
  releasePlot,
  releaseSelection,
  togglePlot,
} from '../lib/plotStore';
import { useInventory, useServerInventory } from '../lib/useStore';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'map', label: 'Site Map & Availability' },
];

const CemeteryDetail = () => {
  const { cemeteryId } = useParams();
  const cemetery = getCemetery(cemeteryId);

  useInventory();
  useServerInventory();
  const [tab, setTab] = useState('overview');
  const [toast, setToast] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(id);
  }, [toast]);

  const handlePlotClick = useCallback(
    (plot) => {
      const result = togglePlot(cemeteryId, plot.id);
      if (!result.ok) setToast(result.reason);
    },
    [cemeteryId],
  );

  if (!cemetery) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <h1 className="font-cormorant text-3xl font-semibold text-navy-900">
          Cemetery not found
        </h1>
        <Link
          to="/cemeteries"
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-navy-800 underline underline-offset-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to all cemeteries
        </Link>
      </div>
    );
  }

  const counts = getAvailability(cemetery);
  const available = counts.available + counts.selected;
  const selectionCount = getSelection(cemetery.id).length;

  return (
    <div className="bg-slate-50 pb-20">
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy-gradient text-white">
        <div className="absolute inset-0 opacity-25">
          <img
            src={cemetery.images[2]?.src || cemetery.images[0].src}
            alt=""
            className="h-full w-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/80 via-navy-950/40 to-transparent" />
        <div className="container relative mx-auto px-4 py-16">
          <Link
            to="/cemeteries"
            className="inline-flex items-center gap-1.5 font-montserrat text-xs font-semibold uppercase tracking-[0.18em] text-navy-200 transition hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            All cemeteries
          </Link>
          <h1 className="mt-4 font-cormorant text-4xl font-semibold text-white sm:text-5xl">
            {cemetery.name}
          </h1>
          <p className="mt-2 flex items-center gap-1.5 font-montserrat text-sm text-navy-100">
            <MapPin className="h-4 w-4" />
            {cemetery.address.street}, {formatLocation(cemetery)}
          </p>

          <dl className="mt-8 flex flex-wrap gap-x-10 gap-y-4">
            <Stat label="Sites available" value={`${available} of ${counts.total}`} />
            <Stat label="Burial sites from" value={formatMoney(cemetery.pricing.burial)} />
            <Stat label="Cremation sites from" value={formatMoney(cemetery.pricing.cremation)} />
            <Stat label="Established" value={cemetery.established} />
          </dl>
        </div>
      </section>

      {/* Tabs */}
      <div className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="container mx-auto flex items-center justify-between gap-4 px-4">
          <nav className="flex" aria-label="Cemetery sections">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                aria-current={tab === t.id ? 'page' : undefined}
                className={`border-b-2 px-1 py-4 text-sm tracking-wide transition sm:px-4 ${
                  tab === t.id
                    ? 'border-navy-800 font-medium text-navy-900'
                    : 'border-transparent text-slate-500 hover:text-navy-800'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>

          {selectionCount > 0 && tab === 'overview' && (
            <button
              type="button"
              onClick={() => setTab('map')}
              className="rounded-md bg-navy-800 px-4 py-2 text-xs font-medium text-white hover:bg-navy-900"
            >
              {selectionCount} site{selectionCount === 1 ? '' : 's'} held — review
            </button>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {tab === 'overview' ? (
          <Overview cemetery={cemetery} onOpenMap={() => setTab('map')} />
        ) : (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <h2 className="font-cormorant text-3xl font-semibold text-navy-900">
                Choose your sites
              </h2>
              <p className="mb-6 mt-1.5 max-w-2xl text-sm leading-relaxed text-slate-500">
                Hover a site to see its row, type, and price. Click to hold it — held sites are
                locked to you for 15 minutes and cannot be claimed by anyone else in the
                meantime. Sold and reserved sites are greyed out.
              </p>
              <PlotMap
                cemetery={cemetery}
                onPlotClick={handlePlotClick}
                onError={(message) => setToast(message)}
              />
            </div>

            <div className="lg:sticky lg:top-20 lg:self-start">
              <SelectionPanel
                cemetery={cemetery}
                onRelease={(plotId) => releasePlot(cemetery.id, plotId)}
                onExtend={() => {
                  extendHolds(cemetery.id);
                  setToast('Your hold has been extended by 15 minutes.');
                }}
                onClearAll={() => releaseSelection(cemetery.id)}
                onCheckout={() => setCheckoutOpen(true)}
              />
            </div>
          </div>
        )}
      </div>

      {checkoutOpen && (
        <CheckoutModal
          cemetery={cemetery}
          onClose={() => setCheckoutOpen(false)}
          onComplete={() => setToast(null)}
        />
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <dt className="text-[10px] uppercase tracking-widest text-navy-200">{label}</dt>
    <dd className="mt-1 font-cormorant text-2xl font-semibold">{value}</dd>
  </div>
);

const Overview = ({ cemetery, onOpenMap }) => (
  <div className="grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,1fr)_320px]">
    <div>
      <h2 className="font-cormorant text-3xl font-semibold text-navy-900">
        About these grounds
      </h2>
      <p className="mt-4 leading-relaxed text-slate-600">{cemetery.description}</p>

      <ul className="mt-8 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
        {cemetery.highlights.map((item) => (
          <li key={item} className="flex gap-2.5 text-sm text-slate-600">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-navy-700" />
            {item}
          </li>
        ))}
      </ul>

      <h2 className="mt-12 font-cormorant text-3xl font-semibold text-navy-900">
        The grounds
      </h2>
      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
        {cemetery.images.map((image) => (
          <figure key={image.src} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <img
              src={image.src}
              alt={image.alt}
              className="aspect-[4/3] w-full bg-slate-50 object-contain"
            />
            <figcaption className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
              {image.caption}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>

    {/* Sidebar */}
    <aside className="space-y-6 lg:sticky lg:top-20 lg:self-start">
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="font-cormorant text-2xl font-semibold text-navy-900">
          Visit &amp; contact
        </h3>
        <dl className="mt-4 space-y-4 text-sm">
          <Detail icon={MapPin} label="Address">
            {cemetery.address.street}
            <br />
            {formatLocation(cemetery)}
            <br />
            <span className="text-slate-500">{cemetery.address.county}</span>
          </Detail>
          <Detail icon={Clock} label="Hours">
            {cemetery.hours}
          </Detail>
          <Detail icon={Phone} label="Phone">
            <a href={`tel:${cemetery.contact.phone}`} className="hover:text-navy-800">
              {cemetery.contact.phone}
            </a>
          </Detail>
          <Detail icon={Mail} label="Email">
            <a
              href={`mailto:${cemetery.contact.email}`}
              className="break-all hover:text-navy-800"
            >
              {cemetery.contact.email}
            </a>
          </Detail>
          <Detail icon={Ruler} label="Grounds">
            {cemetery.acreage} acres · {cemetery.layout.rows.length} rows ·{' '}
            {cemetery.layout.rows.length * cemetery.layout.plotsPerRow} sites
          </Detail>
        </dl>
      </div>

      <div className="rounded-lg border border-navy-200 bg-navy-50 p-6">
        <h3 className="font-cormorant text-2xl font-semibold text-navy-900">Pricing</h3>
        <dl className="mt-4 space-y-2.5 text-sm">
          <div className="flex justify-between">
            <dt className="text-slate-600">Burial site (rows C–N)</dt>
            <dd className="font-medium text-navy-900">
              {formatMoney(cemetery.pricing.burial)}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-600">
              Cremation site (rows {cemetery.layout.cremationRows.join(', ')})
            </dt>
            <dd className="font-medium text-navy-900">
              {formatMoney(cemetery.pricing.cremation)}
            </dd>
          </div>
          <div className="flex justify-between border-t border-navy-200 pt-2.5">
            <dt className="text-slate-600">Perpetual care</dt>
            <dd className="font-medium text-navy-900">
              {Math.round(cemetery.pricing.careFeeRate * 100)}% of site price
            </dd>
          </div>
        </dl>
        <button
          type="button"
          onClick={onOpenMap}
          className="mt-6 w-full rounded-md bg-navy-800 py-3 text-sm font-medium tracking-wide text-white transition hover:bg-navy-900"
        >
          View available sites
        </button>
      </div>
    </aside>
  </div>
);

// A <div> inside <dl> may contain nothing but dt/dd, and dt/dd may not sit any
// deeper than that. The icon previously forced a second wrapper div, which broke
// both rules. Reserving the gutter with padding and absolutely positioning the
// decorative icon out of the dt keeps the exact same layout — icon left,
// label above value, 16px icon + 12px gap = 28px (pl-7).
const Detail = ({ icon: Icon, label, children }) => (
  <div className="relative pl-7">
    <dt className="text-[10px] uppercase tracking-widest text-slate-500">
      <Icon aria-hidden="true" className="absolute left-0 top-0.5 h-4 w-4 text-slate-300" />
      {label}
    </dt>
    <dd className="mt-0.5 leading-relaxed text-slate-600">{children}</dd>
  </div>
);

const Toast = ({ message, onDismiss }) => (
  <div
    role="status"
    className="fixed bottom-6 left-1/2 z-[80] flex w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 items-start gap-3 rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-white shadow-2xl"
  >
    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
    <p className="flex-1 text-sm leading-relaxed">{message}</p>
    <button
      type="button"
      onClick={onDismiss}
      className="rounded p-0.5 text-slate-400 hover:text-white"
      aria-label="Dismiss"
    >
      <X className="h-4 w-4" />
    </button>
  </div>
);

export default CemeteryDetail;
